import { useTraderText } from '../context/TraderLanguageContext';
/**
 * Trader Funds Page
 * Shows live financial status and account transactions
 * Similar to MT5/SiRiX funds interface
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useTraderTheme } from '../context/TraderThemeContext';
import { useTraderLanguage } from '../context/TraderLanguageContext';
import { useTraderAuth, traderAPI } from '../context/TraderAuthContext';
import { watchLiveRefresh } from '../../hooks/liveRefresh';
import './TraderFunds.css';
import { 
  Wallet, TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle,
  Filter, ChevronDown, RefreshCw, Download, Search
} from 'lucide-react';

// Format currency with TL symbol
const formatCurrency = (value, currency, locale) => {
  if (value === null || value === undefined) return `${currency} 0.00`;
  const num = parseFloat(value);
  const formatted = Math.abs(num).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return num < 0 ? `${currency} -${formatted}` : `${currency} ${formatted}`;
};

// Format percentage
const formatPercentage = (value, locale) => {
  if (value === null || value === undefined) return '0.00%';
  return `${parseFloat(value).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}%`;
};

// Format Turkey Time
const formatTurkeyTime = (dateString, locale) => {
  if (!dateString) return '--';
  const date = new Date(dateString);
  return date.toLocaleString(locale, {
    timeZone: 'Europe/Istanbul',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).replace(',', '');
};

const TraderFunds = () => {
  const tx = useTraderText();
  const { isDark } = useTraderTheme();
  const { t, locale } = useTraderLanguage();
  const { user, refreshUser: fetchFinancials } = useTraderAuth();
  const financials = {equity:user?.equity??0,free_margin:user?.free_margin??0,used_margin:user?.used_margin??0,open_pnl:user?.open_pl??0,balance:user?.balance??0,margin_level:user?.margin_level??0,credit:user?.credit??0};
  const loading = !user;
  const currency = user?.currency || 'USD';
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [actionTypeFilter, setActionTypeFilter] = useState('All');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionsError,setTransactionsError] = useState('');
  const pendingRequest = useRef(null);

  // Action type options with translations
  const actionTypes = [
    { value: 'All', label: t('funds.all') },
    { value: 'Deposit', label: t('funds.deposit') },
    { value: 'Withdraw', label: t('funds.withdraw') },
    { value: 'Transfer', label: 'Transfer' }
  ];

  // Fetch transactions
  const fetchTransactions = useCallback(async () => {
    if(pendingRequest.current || !user?.id)return;
    const controller=new AbortController();
    const token=localStorage.getItem('trader_token');
    if(!token)return;
    pendingRequest.current=controller;
    try {
      const {data}=await traderAPI.get('/api/trader/transactions',{headers:{Authorization:`Bearer ${token}`},signal:controller.signal,timeout:20000});
      if (!controller.signal.aborted && localStorage.getItem('trader_token')===token) {
        setTransactions(data);
        setTransactionsError('');
      }
    } catch (error) {
      if(!controller.signal.aborted){setTransactions([]);setTransactionsError('Transactions could not be refreshed.');}
    } finally {
      if(!controller.signal.aborted)setTransactionsLoading(false);
      if(pendingRequest.current===controller)pendingRequest.current=null;
    }
  }, [user?.id]);

  useEffect(() => {
    fetchFinancials();
    fetchTransactions();
    const stop=watchLiveRefresh(fetchTransactions);
    return()=>{stop();pendingRequest.current?.abort();pendingRequest.current=null;};
  }, [fetchFinancials, fetchTransactions]);

  // Filter transactions
  const filteredTransactions = transactions.filter(tx => {
    if (actionTypeFilter === 'All') return true;
    if (actionTypeFilter === 'Transfer') return tx.type?.toLowerCase().startsWith('transfer_');
    return tx.type?.toLowerCase() === (actionTypeFilter==='Withdraw'?'withdrawal':actionTypeFilter.toLowerCase());
  });

  // Pagination
  const totalPages = Math.max(1,Math.ceil(filteredTransactions.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedTransactions = filteredTransactions.slice(startIndex, startIndex + rowsPerPage);
  const exportTransactions=()=>{
    const safe=value=>`"${String(value??'').replace(/^[\s]*([=+@-])/,"'$1").replaceAll('"','""')}"`;
    const rows=[['Time','Type','ID','Amount','Currency','Status','Comment'].map(label=>tx(label)),...filteredTransactions.map(row=>[formatTurkeyTime(row.created_at||row.date,locale),tx(row.type),row.transaction_id||row.id,row.amount,row.currency||currency,tx(row.status),row.comment||row.notes])];
    const url=URL.createObjectURL(new Blob(['\uFEFF'+rows.map(row=>row.map(safe).join(',')).join('\r\n')],{type:'text/csv;charset=utf-8'}));
    const link=document.createElement('a');link.href=url;link.download='account-transactions.csv';link.click();
    setTimeout(()=>URL.revokeObjectURL(url),1000);
  };

  // Financial card component
  const FinancialCard = ({ id, label, value, isNegative = false, isPercentage = false }) => (
    <div className={`px-4 py-3 border-r last:border-r-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
      <div className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        {tx(label)}
      </div>
      <div data-testid={`trader-funds-${id}`} className={`font-mono text-sm font-medium ${
        isNegative 
          ? 'text-red-500' 
          : isDark ? 'text-white' : 'text-gray-900'
      }`}>
        {isPercentage ? formatPercentage(value, locale) : formatCurrency(value, currency, locale)}
      </div>
    </div>
  );

  return (
    <div className={`trader-funds-page min-h-screen ${isDark ? 'bg-[#0a0e17]' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('funds.title')}
          </h1>
          <button
            data-testid="trader-funds-refresh"
            onClick={() => { fetchFinancials(); fetchTransactions(); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              isDark 
                ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <RefreshCw size={16} />
            {t('funds.refresh')}
          </button>
        </div>

        {/* Financial Summary Cards */}
        <div className={`rounded-xl mb-6 ${isDark ? 'bg-[#111827] border border-gray-800' : 'bg-white border border-gray-200'}`}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="animate-spin text-cyan-500" size={24} />
            </div>
          ) : (
            <div className="flex flex-wrap">
              <FinancialCard id="equity" label={t('funds.equity')} value={financials.equity} />
              <FinancialCard id="free-margin" label={t('funds.freeMargin')} value={financials.free_margin} />
              <FinancialCard id="used-margin" label={t('funds.usedMargin')} value={financials.used_margin} />
              <FinancialCard 
                id="open-pl" label={t('funds.openPL')} 
                value={financials.open_pnl} 
                isNegative={financials.open_pnl < 0}
              />
              <FinancialCard id="balance" label={t('funds.balance')} value={financials.balance} />
              <FinancialCard 
                id="margin-level" label={t('funds.marginLevel')} 
                value={financials.margin_level} 
                isPercentage={true}
              />
              <FinancialCard id="credit" label={t('funds.credit')} value={financials.credit} />
            </div>
          )}
        </div>

        {/* Account Transactions */}
        <div className={`rounded-xl ${isDark ? 'bg-[#111827] border border-gray-800' : 'bg-white border border-gray-200'}`}>
          {/* Header */}
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t('funds.accountTransactions')}
                </h2>
                <span data-testid="trader-funds-transaction-count" className="px-2 py-0.5 text-xs font-medium bg-blue-500 text-white rounded-full">
                  {filteredTransactions.length}
                </span>
              </div>
              <button
                data-testid="trader-funds-export"
                onClick={exportTransactions}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
                  isDark 
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Download size={14} />
                {t('funds.export')}
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center gap-2 mb-3">
              <Filter size={14} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
              <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('funds.filters')}
              </span>
            </div>
            
            {/* Action Type Filter */}
            <div className="relative inline-block">
              <button
                data-testid="trader-funds-filter-toggle"
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm border ${
                  isDark 
                    ? 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700' 
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t('funds.actionType')}
                {actionTypeFilter !== 'All' && (
                  <span className="px-1.5 py-0.5 text-xs bg-cyan-500 text-white rounded">
                    {tx(actionTypes.find(a => a.value === actionTypeFilter)?.label)}
                  </span>
                )}
                <ChevronDown size={14} />
              </button>
              
              {showFilterDropdown && (
                <div className={`absolute top-full left-0 mt-1 w-40 rounded-lg shadow-lg z-10 ${
                  isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                }`}>
                  {actionTypes.map(type => (
                    <button
                      data-testid={`trader-funds-filter-${type.value.toLowerCase()}`}
                      key={type.value}
                      onClick={() => {
                        setActionTypeFilter(type.value);
                        setShowFilterDropdown(false);
                        setCurrentPage(1);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm ${
                        actionTypeFilter === type.value
                          ? 'bg-cyan-500/20 text-cyan-400'
                          : isDark 
                            ? 'text-gray-300 hover:bg-gray-700' 
                            : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {tx(type.label)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Transactions Table */}
          {transactionsError&&<p role="alert" data-testid="trader-funds-error" className="px-4 py-2 text-red-500">{tx(transactionsError)}</p>}
          <div className="overflow-x-auto">
            <table className="trader-funds-transactions w-full" data-testid="trader-funds-transactions">
              <thead>
                <tr className={isDark ? 'bg-gray-800/50' : 'bg-gray-50'}>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('funds.time')}
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('funds.actionType')}
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('funds.id')}
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('funds.amount')}
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('funds.comment')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {transactionsLoading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center">
                      <RefreshCw className="animate-spin text-cyan-500 mx-auto" size={24} />
                    </td>
                  </tr>
                ) : paginatedTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className={`px-4 py-8 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {t('funds.noTransactions')}
                    </td>
                  </tr>
                ) : (
                  paginatedTransactions.map((tx, index) => {
                    const isTransfer = tx.type?.toLowerCase().startsWith('transfer_');
                    const isDeposit = ['deposit','transfer_in'].includes(tx.type?.toLowerCase());
                    const amount = parseFloat(tx.amount || 0);
                    
                    return (
                      <tr 
                        data-testid={`trader-funds-transaction-${tx.id||index}`}
                        key={tx.id || index}
                        className={`${isDark ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'} transition-colors`}
                      >
                        <td data-label={t('funds.time')} className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {formatTurkeyTime(tx.created_at || tx.date, locale)}
                        </td>
                        <td data-label={t('funds.actionType')} className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                            isDeposit 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {isDeposit ? <ArrowDownCircle size={12} /> : <ArrowUpCircle size={12} />}
                            {isTransfer ? (isDeposit ? 'Transfer ↓' : 'Transfer ↑') : isDeposit ? t('funds.deposit') : t('funds.withdraw')}
                          </span>
                        </td>
                        <td data-label={t('funds.id')} className={`px-4 py-3 text-sm font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {tx.transaction_id || tx.id || '--'}
                        </td>
                        <td data-label={t('funds.amount')} data-testid={`trader-funds-amount-${tx.id||index}`} className={`px-4 py-3 text-sm font-mono font-medium ${
                          isDeposit ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {isDeposit 
                            ? formatCurrency(amount, tx.currency || currency, locale)
                            : formatCurrency(-Math.abs(amount), tx.currency || currency, locale)
                          }
                        </td>
                        <td data-label={t('funds.comment')} className={`px-4 py-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {tx.comment || tx.notes || '--'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className={`flex flex-wrap gap-3 items-center justify-between px-4 py-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('funds.rowsPerPage')}:
              </span>
              <select
                data-testid="trader-funds-page-size"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className={`px-2 py-1 rounded text-sm ${
                  isDark 
                    ? 'bg-gray-800 border-gray-600 text-gray-300' 
                    : 'bg-white border-gray-300 text-gray-700'
                } border`}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            
            <div className="flex items-center gap-4">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {filteredTransactions.length?startIndex + 1:0}-{Math.min(startIndex + rowsPerPage, filteredTransactions.length)} {t('funds.of')} {filteredTransactions.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  data-testid="trader-funds-prev"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`p-1 rounded ${
                    currentPage === 1
                      ? 'text-gray-600 cursor-not-allowed'
                      : isDark 
                        ? 'text-gray-400 hover:bg-gray-700' 
                        : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  ‹
                </button>
                <button
                  data-testid="trader-funds-next"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className={`p-1 rounded ${
                    currentPage >= totalPages
                      ? 'text-gray-600 cursor-not-allowed'
                      : isDark 
                        ? 'text-gray-400 hover:bg-gray-700' 
                        : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TraderFunds;
